/**
 * Sending settings and brand of one owner (the platform, or a customer whose tier lets it send
 * under its own brand). The SMTP password is write-only: the form only says whether one is saved.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { EmailBrand, EmailSettingsInput, EmailSettingsView, SmtpSecurity } from '../../api/areas/notify';
import { Badge } from '../../components/Badge';
import { Callout, Field } from '../../components/blocks';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { Panel, SwitchRow } from '../integrations/shared';

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30';

const BRAND_FIELDS: (keyof EmailBrand)[] = ['name', 'website', 'support_email', 'support_phone', 'logo_url', 'primary_color', 'address', 'footer', 'action_base_url'];

type Draft = Omit<EmailSettingsInput, 'password'> & { passwordAction: 'KEEP' | 'SET' | 'CLEAR'; password: string };

function draftOf(v: EmailSettingsView): Draft {
  const s = v.settings;
  return {
    enabled: s?.enabled ?? true,
    smtp_host: s?.smtp_host ?? '',
    smtp_port: s?.smtp_port ?? 587,
    smtp_security: s?.smtp_security ?? 'STARTTLS',
    smtp_username: s?.smtp_username ?? '',
    from_address: s?.from_address ?? '',
    from_name: s?.from_name ?? '',
    reply_to: s?.reply_to ?? '',
    bcc_address: s?.bcc_address ?? '',
    brand: { ...(s?.brand ?? {}) },
    expected_version: s?.version,
    passwordAction: s?.has_password ? 'KEEP' : 'SET',
    password: '',
  };
}

function Form({ owner, view, onSaved }: { owner: string; view: EmailSettingsView; onSaved: () => void }) {
  const { t } = useTranslation('integrations');
  const [d, setD] = useState<Draft>(() => draftOf(view));
  const [testTo, setTestTo] = useState('');
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const save = useWrite((b: EmailSettingsInput, opts) => api.saveEmailSettings(owner, b, opts));
  const test = useWrite((b: { to: string }, opts) => api.testEmailSettings(owner, b, opts));
  const hasPassword = view.settings?.has_password ?? false;
  const submit = async () => {
    const body: EmailSettingsInput = {
      enabled: d.enabled,
      smtp_host: d.smtp_host,
      smtp_port: Number(d.smtp_port) || 587,
      smtp_security: d.smtp_security,
      smtp_username: d.smtp_username?.trim() || null,
      password: d.passwordAction === 'SET' ? { action: 'SET', value: d.password } : { action: d.passwordAction },
      from_address: d.from_address,
      from_name: d.from_name,
      reply_to: d.reply_to?.trim() || null,
      bcc_address: d.bcc_address?.trim() || null,
      brand: d.brand,
      expected_version: d.expected_version,
    };
    const r = await save.run(body);
    if (r) {
      set({ password: '', passwordAction: r.settings?.has_password ? 'KEEP' : 'SET', expected_version: r.settings?.version });
      onSaved();
    }
  };
  return (
    <form
      className="grid gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <SwitchRow label={t('email.enabled')} hint={<span className="text-xs text-muted-foreground">{t('email.enabledHint')}</span>} checked={d.enabled} onChange={(v) => set({ enabled: v })} />
      <fieldset className="grid gap-3 sm:grid-cols-3">
        <legend className="mb-2 text-sm font-medium">{t('email.server')}</legend>
        <Field label={t('email.host')} className="sm:col-span-2">
          <Input value={d.smtp_host} onChange={(e) => set({ smtp_host: e.target.value })} placeholder="smtp.example.vn" required />
        </Field>
        <Field label={t('email.port')}>
          <Input inputMode="numeric" value={d.smtp_port} onChange={(e) => set({ smtp_port: Number(e.target.value.replace(/\D/g, '')) || 0 })} />
        </Field>
        <Field label={t('email.security')}>
          <select className={selectClass} value={d.smtp_security} onChange={(e) => set({ smtp_security: e.target.value as SmtpSecurity })}>
            <option value="STARTTLS">{t('email.securities.STARTTLS')}</option>
            <option value="TLS">{t('email.securities.TLS')}</option>
            <option value="NONE">{t('email.securities.NONE')}</option>
          </select>
        </Field>
        <Field label={t('email.username')}>
          <Input value={d.smtp_username ?? ''} onChange={(e) => set({ smtp_username: e.target.value })} autoComplete="off" />
        </Field>
        <Field label={t('email.password')} hint={hasPassword ? t('email.passwordSaved') : t('email.passwordNone')}>
          <div className="flex gap-2">
            <select className={selectClass} value={d.passwordAction} onChange={(e) => set({ passwordAction: e.target.value as Draft['passwordAction'] })}>
              {hasPassword ? <option value="KEEP">{t('email.passwordKeep')}</option> : null}
              <option value="SET">{hasPassword ? t('email.passwordReplace') : t('email.passwordSet')}</option>
              {hasPassword ? <option value="CLEAR">{t('email.passwordClear')}</option> : null}
            </select>
          </div>
        </Field>
        {d.passwordAction === 'SET' ? (
          <Field label={t('email.newPassword')} className="sm:col-span-3">
            <Input type="password" value={d.password} onChange={(e) => set({ password: e.target.value })} autoComplete="new-password" />
          </Field>
        ) : null}
      </fieldset>
      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-medium">{t('email.sender')}</legend>
        <Field label={t('email.fromName')}>
          <Input value={d.from_name} onChange={(e) => set({ from_name: e.target.value })} required />
        </Field>
        <Field label={t('email.fromAddress')}>
          <Input type="email" value={d.from_address} onChange={(e) => set({ from_address: e.target.value })} required />
        </Field>
        <Field label={t('email.replyTo')}>
          <Input type="email" value={d.reply_to ?? ''} onChange={(e) => set({ reply_to: e.target.value })} />
        </Field>
        <Field label={t('email.bcc')} hint={t('email.bccHint')}>
          <Input type="email" value={d.bcc_address ?? ''} onChange={(e) => set({ bcc_address: e.target.value })} />
        </Field>
      </fieldset>
      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-medium">{t('email.brand')}</legend>
        {BRAND_FIELDS.map((k) => (
          <Field key={k} label={t(`email.brandFields.${k}`)} hint={k === 'action_base_url' ? t('email.actionBaseHint') : k === 'primary_color' ? '#1d4ed8' : undefined}>
            <div className="flex items-center gap-2">
              {k === 'primary_color' ? <span className="size-6 shrink-0 rounded border" style={{ background: d.brand.primary_color || '#1d4ed8' }} aria-hidden="true" /> : null}
              <Input value={d.brand[k] ?? ''} onChange={(e) => set({ brand: { ...d.brand, [k]: e.target.value } })} />
            </div>
          </Field>
        ))}
      </fieldset>
      {save.state.phase === 'error' ? <WriteError error={save.state.error} /> : null}
      {save.state.phase === 'done' ? <Callout tone="ok">{t('email.saved')}</Callout> : null}
      <div className="flex flex-wrap items-end gap-3">
        <Button type="submit" disabled={save.busy}>
          {t('email.save')}
        </Button>
        <div className="flex flex-1 flex-wrap items-end gap-2">
          <Field label={t('email.testTo')} className="min-w-56 flex-1">
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="ban@example.vn" />
          </Field>
          <Button type="button" variant="outline" disabled={!view.settings || test.busy || !testTo.includes('@')} onClick={() => void test.run({ to: testTo })}>
            {t('email.sendTest')}
          </Button>
        </div>
      </div>
      {test.state.phase === 'error' ? <WriteError error={test.state.error} /> : null}
      {test.state.phase === 'done' ? <Callout tone="ok">{t('email.testSent', { answer: test.state.result.server_answer })}</Callout> : null}
    </form>
  );
}

/** Settings panel for `owner` ('platform' or a customer id). */
export function EmailSettingsPanel({ owner }: { owner: string }) {
  const { t } = useTranslation('integrations');
  const r = useApi((signal) => api.getEmailSettings(owner, { signal }), [owner]);
  const isPlatform = owner === 'platform';
  return (
    <Panel
      title={isPlatform ? t('email.platformTitle') : t('email.customerTitle')}
      description={isPlatform ? t('email.platformDescription') : t('email.customerDescription')}
      actions={
        r.status === 'success' ? (
          <Badge tone={r.data.sending_with === 'NONE' ? 'warn' : 'ok'}>{t(`email.sendingWith.${r.data.sending_with}`)}</Badge>
        ) : null
      }
    >
      {r.status === 'loading' ? <Loading rows={3} /> : null}
      {r.status === 'error' ? <WriteError error={r.error} /> : null}
      {r.status === 'success' ? (
        <>
          {r.data.settings ? (
            <p className="mb-4 text-xs text-muted-foreground">{t('email.lastSaved', { when: formatDateTime(r.data.settings.updated_at), who: r.data.settings.updated_by })}</p>
          ) : !isPlatform ? (
            <Callout tone="info" className="mb-4">
              {r.data.sending_with === 'PLATFORM' ? t('email.usesPlatform') : t('email.noneYet')}
            </Callout>
          ) : (
            <Callout tone="warn" className="mb-4">
              {t('email.platformMissing')}
            </Callout>
          )}
          <Form key={r.data.settings?.version ?? 0} owner={owner} view={r.data} onSaved={r.reload} />
        </>
      ) : null}
    </Panel>
  );
}
